import React, { Component } from 'react';
import Overlay from 'components/Overlay';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { classNameWithModifiers as withMods } from 'helpers/class-name';

import './Dropdown.scss';

export const DropdownPropTypes = {
  attachment: PropTypes.oneOf(['left', 'right', 'center']),
  usePortal: PropTypes.bool,
  noStyle: PropTypes.bool,
  isOpen: PropTypes.bool,
  isDesktop: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  closeOnScroll: PropTypes.bool,
  Toggle: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  Content: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  className: PropTypes.string,
  portalId: PropTypes.string,
  contentClassName: PropTypes.string,
  disabled: PropTypes.bool,
  onClose: PropTypes.func,
  useOverlay: PropTypes.bool,
  shouldCloseContentOnChange: PropTypes.bool,
  dataCypress: PropTypes.string,
};

class Dropdown extends Component {
  static propTypes = DropdownPropTypes;

  static defaultProps = {
    attachment: 'center',
    noStyle: false,
    closeOnClick: true,
    closeOnEsc: true,
    closeOnScroll: false,
    portalId: 'dropdown__host',
    usePortal: true,
    useOverlay: true,
    Toggle: () => null,
    Content: () => null,
    shouldCloseContentOnChange: true,
    dataCypress: 'dropdown',
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: props.isOpen || false,
      isScrolled: false,
    };

    this.container = React.createRef();
    this.scrollContainer = React.createRef();
    this.selectedItem = React.createRef();
  }

  componentDidMount() {
    const { closeOnClick, closeOnEsc, shouldCloseContentOnChange } = this.props;

    if (closeOnClick && shouldCloseContentOnChange) {
      window.addEventListener('click', this.handleCloseEvent);
    }

    if (closeOnEsc) {
      window.addEventListener('keyup', this.handleKeyEvent);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen: wasOpen } = prevState;
    const { isOpen } = this.state;
    const { closeOnClick: prevCloseOnClick } = prevProps;
    const { closeOnClick } = this.props;
    const { current: scrollContainer } = this.scrollContainer;

    if (scrollContainer) {
      if (!wasOpen && isOpen) {
        scrollContainer.addEventListener('scroll', this.handleContentScroll);
      } else if (wasOpen && !isOpen) {
        scrollContainer.removeEventListener('scroll', this.handleContentScroll);
      }
    }

    if (closeOnClick === prevCloseOnClick) return;

    if (closeOnClick) {
      window.addEventListener('click', this.handleCloseEvent);
    } else {
      window.removeEventListener('click', this.handleCloseEvent);
    }
  }

  componentWillUnmount() {
    const { closeOnClick, closeOnEsc } = this.props;

    if (closeOnClick) {
      window.removeEventListener('click', this.handleCloseEvent);
    }

    if (closeOnEsc) {
      window.removeEventListener('keyup', this.handleKeyEvent);
    }
  }

  toggleMenu = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { disabled, onClose } = this.props;

    if (!disabled) {
      this.setState(
        ({ isOpen }) => ({
          isOpen: !isOpen,
        }),
        () => {
          const { isOpen } = this.state;
          if (isOpen) {
            this.adjustSizeAndPosition();
            this.scrollToSelectedItem();
          } else if (onClose) {
            onClose();
          }
        }
      );
    }
  };

  closeMenu = () => {
    const { onClose } = this.props;

    this.setState(
      {
        isOpen: false,
      },
      () => {
        if (onClose) {
          onClose();
        }
      }
    );
  };

  handleContentScroll = () => {
    const { current: scrollContainer } = this.scrollContainer;
    // In cypress tests we're experiencing an issue where scroll event is triggered on unmounted container
    // which results in scrollContainer beeing undefined here
    if (!scrollContainer) return;
    this.setState({
      isScrolled: scrollContainer.scrollTop > 0,
    });
  };

  handleCloseEvent = event => {
    const { isOpen } = this.state;
    const { current: container } = this.container;
    const { target } = event;

    if (isOpen && container && (container === target || !container.contains(target))) {
      this.closeMenu();
    }
  };

  handleScreenClick = event => {
    event.stopPropagation();
    this.handleCloseEvent(event);
  };

  handleKeyEvent = event => {
    const { isOpen } = this.state;
    const { keyCode } = event;

    // esc
    if (isOpen && keyCode === 27) {
      this.handleCloseEvent(event);
    }
  };

  adjustSizeAndPosition() {
    const { usePortal, isDesktop, attachment } = this.props;

    if (!usePortal || !isDesktop) {
      return;
    }

    const {
      top: toggleTop,
      right: toggleRight,
      bottom: toggleBottom,
      left: toggleLeft,
      width: toggleWidth,
    } = this.container.current.getBoundingClientRect();
    const { width: contentWidth, height: contentHeight } = this.scrollContainer.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const contentStyle = window.getComputedStyle(this.scrollContainer.current);
    const verticalMargins =
      Number.parseInt(contentStyle.marginTop, 10) + Number.parseInt(contentStyle.marginBottom, 10);
    const derivedContentWidth = Math.max(
      Math.floor(toggleWidth),
      Math.floor(contentWidth),
      Number.parseInt(contentStyle.minWidth, 10)
    );

    const contentDoesntFitOnScreen = contentHeight > windowHeight;
    const contentDoesntFitBelowTheToggle = contentHeight > windowHeight - toggleBottom;
    const contentDoesntFitAboveTheToggle = contentHeight > toggleTop;

    if (contentDoesntFitOnScreen) {
      // constrain to the top and bottom of the screen
      this.scrollContainer.current.style.top = '0px';
      this.scrollContainer.current.style.height = `${windowHeight - verticalMargins}px`;
    } else if (contentDoesntFitBelowTheToggle && contentDoesntFitAboveTheToggle) {
      // attach to the bottom of the screen and extend up
      this.scrollContainer.current.style.top = `${windowHeight - contentHeight - verticalMargins}px`;
    } else if (contentDoesntFitBelowTheToggle) {
      // attach to the top of the toggle and extend up
      this.scrollContainer.current.style.bottom = `${windowHeight - toggleTop}px`;
    } else {
      // attach to the bottom of the toggle and extend down
      this.scrollContainer.current.style.top = `${toggleBottom}px`;
    }

    this.scrollContainer.current.style.position = 'fixed';
    this.scrollContainer.current.style.minWidth = `${derivedContentWidth}px`;

    switch (attachment) {
      case 'left':
        this.scrollContainer.current.style.left = `${toggleLeft}px`;
        break;

      case 'right':
        this.scrollContainer.current.style.left = `${toggleRight - derivedContentWidth}px`;
        break;

      default:
        // 'center'
        this.scrollContainer.current.style.left = `${
          toggleLeft - Math.floor(derivedContentWidth / 2) + Math.floor(toggleWidth / 2)
        }px`;
        break;
    }
  }

  scrollToSelectedItem() {
    const {
      selectedItem: { current: selectedItem },
      scrollContainer: { current: scrollContainer },
    } = this;

    if (selectedItem && scrollContainer) {
      const { offsetHeight: scrollContainerHeight } = scrollContainer;
      const { offsetHeight: selectedItemHeight } = selectedItem;

      // Center the selected item in the scroll container
      const scrollTop = selectedItem.offsetTop - scrollContainerHeight / 2 + selectedItemHeight / 2;
      scrollContainer.scrollTop = scrollTop;
    }
  }

  renderToggle() {
    // destructuring, but not using, `className` so we don't pass it to the Toggle
    const { disabled, Toggle, noStyle, className, ...restProps } = this.props;
    const { isOpen } = this.state;
    const classes = classNames('dropdown__toggle', {
      'dropdown__toggle--no-style': noStyle,
      'dropdown__toggle--disabled': disabled,
    });

    return (
      <div className={classes}>
        <Toggle
          {...restProps}
          toggleMenu={this.toggleMenu}
          closeMenu={this.closeMenu}
          isOpen={isOpen}
          disabled={disabled}
        />
      </div>
    );
  }

  renderContent() {
    // destructuring, but not using, `className` so we don't pass it to the Content
    const {
      Content,
      attachment,
      noStyle,
      className,
      closeOnScroll,
      portalId,
      usePortal,
      useOverlay,
      contentClassName,
      ...restProps
    } = this.props;
    const { isOpen, isScrolled } = this.state;
    const containerClasses = withMods('dropdown__content', { isScrolled, noStyle, usePortal }, contentClassName);
    const element = document.getElementById(portalId);

    let content = null;
    if (isOpen) {
      content = [
        <Overlay
          key="overlay"
          className="dropdown__overlay"
          closeOnScroll={closeOnScroll}
          onClick={this.handleScreenClick}
        />,
        <div key="container" ref={this.scrollContainer} className={containerClasses}>
          <Content
            selectedRef={this.selectedItem}
            {...restProps}
            toggleMenu={this.toggleMenu}
            closeMenu={this.closeMenu}
            isOpen={isOpen}
          />
        </div>,
      ];

      if (!useOverlay || !usePortal || !element) {
        content.shift();
      }
    }

    if (!usePortal || !element) {
      return content;
    }

    return ReactDOM.createPortal(content, element);
  }

  render() {
    const { className, noStyle, dataCypress } = this.props;
    const { isOpen } = this.state;
    const classes = classNames('dropdown', className, {
      'dropdown--no-style': noStyle,
      'dropdown--is-open': isOpen,
    });

    return (
      <div ref={this.container} className={classes} data-cypress={dataCypress}>
        {this.renderToggle()}
        {this.renderContent()}
      </div>
    );
  }
}

export default Dropdown;
